import React, { useState } from 'react';
import {
  Sparkles, Users, TrendingUp, MessageCircle, Tv,
  Heart, BarChart3, Share2, CheckCircle, ArrowRight,
  Building2
} from 'lucide-react';
import { toast } from 'sonner';
import ProductShowcase from '../components/ProductShowcase';
import SocialMediaShowcase from '../components/SocialMediaShowcase';
import {
  DeliveryOptions, Stats, Testimonial, Pricing, Founder,
  DemoForm, CTA, Footer
} from '../components/BrochureBottom';

const Brochure = () => {
  const [demoForm, setDemoForm] = useState({
    name: '',
    email: '',
    hotelName: '',
    phone: '',
    rooms: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleDemoSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1000));

    toast.success("Thank you! We'll contact you within 24 hours.");
    setDemoForm({ name: '', email: '', hotelName: '', phone: '', rooms: '', message: '' });
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Hero Section */}
      <section className="relative bg-stone-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-64 h-64 bg-amber-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-stone-600 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-6xl mx-auto px-8 py-24 md:py-32">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-white/10 backdrop-blur rounded-xl flex items-center justify-center">
              <Sparkles className="w-7 h-7 text-amber-400" />
            </div>
            <span className="text-3xl font-light tracking-tight">Anamaya</span>
          </div>

          <h1 className="text-4xl md:text-6xl lg:text-7xl font-light leading-tight mb-6 max-w-4xl">
            Less standardisation,<br />
            <span className="text-amber-400">more personalisation</span><br />
            with AI.
          </h1>

          <p className="text-xl md:text-2xl text-stone-300 font-light max-w-2xl mb-12">
            The first guest experience platform that transforms reviews into remarkable,
            personalized moments your guests will never forget.
          </p>

          <div className="flex flex-wrap gap-4">
            <a href="#demo" className="bg-amber-500 hover:bg-amber-400 text-stone-900 px-8 py-4 rounded-full font-medium transition-colors flex items-center gap-2">
              Request Demo <ArrowRight className="w-5 h-5" />
            </a>
            <a href="#pricing" className="bg-white/10 hover:bg-white/20 backdrop-blur px-8 py-4 rounded-full font-medium transition-colors">
              View Pricing
            </a>
          </div>
        </div>
      </section>

      {/* Problem Statement */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-6xl mx-auto px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-amber-600 font-medium tracking-wide uppercase text-sm">The Gap</span>
              <h2 className="text-3xl md:text-4xl font-light text-stone-900 mt-4 mb-6 leading-tight">
                Hotels excel at operations.<br />
                <span className="text-stone-400">Guest personalisation? Not so much.</span>
              </h2>
              <p className="text-lg text-stone-600 leading-relaxed">
                Your property runs on advanced PMS, revenue management systems, and CRMs.
                Yet the most crucial element — making each guest feel truly seen — remains an afterthought.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: Building2, label: 'PMS Systems', status: 'Advanced' },
                { icon: TrendingUp, label: 'Revenue Mgmt', status: 'Advanced' },
                { icon: Users, label: 'CRM Tools', status: 'Advanced' },
                { icon: Heart, label: 'Personalisation', status: 'Missing', highlight: true }
              ].map((item, i) => (
                <div
                  key={i}
                  className={`p-6 rounded-2xl ${item.highlight ? 'bg-amber-50 border-2 border-amber-200' : 'bg-stone-50'}`}
                >
                  <item.icon className={`w-8 h-8 mb-3 ${item.highlight ? 'text-amber-600' : 'text-stone-400'}`} />
                  <div className="font-medium text-stone-900">{item.label}</div>
                  <div className={`text-sm ${item.highlight ? 'text-amber-600 font-medium' : 'text-stone-500'}`}>
                    {item.status}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Solution */}
      <section className="py-20 md:py-28 bg-stone-900 text-white">
        <div className="max-w-6xl mx-auto px-8 text-center">
          <span className="text-amber-400 font-medium tracking-wide uppercase text-sm">The Solution</span>
          <h2 className="text-3xl md:text-5xl font-light mt-4 mb-6 max-w-3xl mx-auto">
            Turn guest reviews into<br />
            <span className="text-amber-400">unforgettable experiences</span>
          </h2>
          <p className="text-xl text-stone-300 max-w-2xl mx-auto mb-16">
            Anamaya reads between the lines of every review, building rich guest profiles
            that power truly personalized "Awe Stories" — moments that make guests feel like royalty.
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: '01', title: 'Analyse', desc: 'AI extracts likes, dislikes, patterns & habits from reviews' },
              { step: '02', title: 'Generate', desc: 'Create personalized Awe Stories within your budget' },
              { step: '03', title: 'Delight', desc: 'Execute moments that turn guests into advocates' }
            ].map((item, i) => (
              <div key={i} className="text-left">
                <div className="text-5xl font-light text-amber-400/30 mb-4">{item.step}</div>
                <h3 className="text-2xl font-medium mb-2">{item.title}</h3>
                <p className="text-stone-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section id="features" className="py-20 md:py-28 bg-white">
        <div className="max-w-6xl mx-auto px-8">
          <div className="text-center mb-16">
            <span className="text-amber-600 font-medium tracking-wide uppercase text-sm">Features</span>
            <h2 className="text-3xl md:text-5xl font-light text-stone-900 mt-4">
              Everything you need to<br />personalise at scale
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: Users,
                title: 'Guest Preferences Directory',
                desc: 'Build a comprehensive profile for every guest — their likes, dislikes, dietary preferences, room choices, and celebration dates.',
                bullets: ['Auto-extracted from reviews', 'Staff can add observations', 'Syncs across visits']
              },
              {
                icon: Sparkles,
                title: 'AI Awe Story Generator',
                desc: 'Generate budget-conscious personalization ideas that create lasting memories without breaking the bank.',
                bullets: ['GM sets budget limits', 'AI respects constraints', 'Track ROI per story']
              },
              {
                icon: BarChart3,
                title: 'Performance Analytics',
                desc: 'Measure impact with hotel rankings, employee leaderboards, and story performance metrics.',
                bullets: ['Hotel rankings by region', 'Employee leaderboards', 'Story success rates']
              },
              {
                icon: Share2,
                title: 'Social Media Automation',
                desc: 'Transform positive reviews into stunning social posts automatically — complete with visuals and guest testimonials.',
                bullets: ['Auto-extract keywords', 'Generate branded visuals', 'Schedule & publish']
              },
              {
                icon: MessageCircle,
                title: 'WhatsApp Welcome',
                desc: 'For budget-conscious properties — send personalized WhatsApp messages that make guests feel special before they even arrive.',
                bullets: ['Pre-arrival greetings', 'Name & preferences included', 'Zero additional cost']
              },
              {
                icon: Tv,
                title: 'Smart TV Greetings',
                desc: 'For tech-forward hotels — display personalized welcome messages on in-room smart TVs the moment guests enter.',
                bullets: ['Custom welcome screens', 'Guest name & occasion', 'Integrates with IPTV']
              }
            ].map((feature, idx) => (
              <div key={idx} className="group p-8 rounded-3xl bg-stone-50 hover:bg-amber-50 transition-colors duration-300">
                <div className="w-14 h-14 bg-amber-100 group-hover:bg-amber-200 rounded-2xl flex items-center justify-center mb-6 transition-colors">
                  <feature.icon className="w-7 h-7 text-amber-600" />
                </div>
                <h3 className="text-xl font-medium text-stone-900 mb-3">{feature.title}</h3>
                <p className="text-stone-600 leading-relaxed">{feature.desc}</p>
                <ul className="mt-4 space-y-2">
                  {feature.bullets.map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-stone-500">
                      <CheckCircle className="w-4 h-4 text-amber-500" /> {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Showcase - WOW Story Generator */}
      <ProductShowcase />

      {/* Social Media Showcase */}
      <SocialMediaShowcase />

      {/* Delivery Options */}
      <DeliveryOptions />

      {/* Stats */}
      <Stats />

      {/* Testimonial */}
      <Testimonial />

      {/* Pricing */}
      <Pricing />

      {/* Founder */}
      <Founder />

      {/* Demo Form */}
      <DemoForm
        demoForm={demoForm}
        setDemoForm={setDemoForm}
        handleDemoSubmit={handleDemoSubmit}
        submitting={submitting}
      />

      {/* CTA */}
      <CTA />

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Brochure;
